import React, { Component } from 'react';
import { X, Home, Calendar, MapPin, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Booking } from '../types/booking';
import { motion, AnimatePresence } from 'framer-motion';
interface ReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: Booking;
}
export function ReturnModal({
  isOpen,
  onClose,
  booking
}: ReturnModalProps) {
  const handleGetDirections = () => {
    // Open Google Maps with WMF location
    const address = 'Stanthorpe Waste Management Facility, Queensland';
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };
  const handleNotifyReturn = () => {
    const subject = `Equipment Returned - Booking #${booking.id}`;
    const body = `Hi Team,

I confirm that I have returned the Andros Ultra Binder to Stanthorpe WMF.

Return Details:
- Farmer: ${booking.userName} (${booking.farm || booking.location})
- Return Date: ${new Date().toLocaleDateString('en-AU')}
- Return Time: [Please fill in time]
- Equipment Condition: [Please describe: Good/Fair/Needs Attention]
- Keys returned to: [Office/Bay/etc]

Additional Notes:
[Any issues or observations]

Best regards,
${booking.userName}`;
    window.location.href = `mailto:agplastic.SDRC@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  // Calculate if overdue
  const isOverdue = () => {
    if (!booking.returnDeadline) return false;
    const [day, month, year] = booking.returnDeadline.split('/');
    const deadline = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return new Date() > deadline;
  };
  const getDaysUntilDeadline = () => {
    if (!booking.returnDeadline) return null;
    const [day, month, year] = booking.returnDeadline.split('/');
    const deadline = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();
    const diffTime = deadline.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  const daysUntil = getDaysUntilDeadline();
  const overdue = isOverdue();
  return <AnimatePresence data-id="element-354">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/50 z-50" data-id="element-355" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-356">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-id="element-357">
              {/* Header */}
              <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between" data-id="element-358">
                <div className="flex items-center gap-3" data-id="element-359">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${overdue ? 'bg-red-100' : 'bg-orange-100'}`} data-id="element-360">
                    <Home className={overdue ? 'text-red-600' : 'text-orange-600'} size={20} data-id="element-361" />
                  </div>
                  <div data-id="element-362">
                    <h2 className="text-lg font-bold text-slate-900" data-id="element-363">
                      Return Instructions
                    </h2>
                    <p className="text-xs text-slate-500" data-id="element-364">
                      Return to Stanthorpe WMF
                    </p>
                  </div>
                </div>
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-365">
                  <X size={20} data-id="element-366" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6" data-id="element-367">
                {/* Deadline Alert */}
                {booking.returnDeadline && <div className={`border rounded-lg p-4 ${overdue ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`} data-id="element-368">
                    <div className="flex items-start gap-2" data-id="element-369">
                      {overdue ? <AlertCircle className="text-red-600 mt-0.5" size={20} data-id="element-370" /> : <Clock className="text-orange-600 mt-0.5" size={20} data-id="element-371" />}
                      <div data-id="element-372">
                        <h3 className={`font-semibold ${overdue ? 'text-red-900' : 'text-orange-900'}`} data-id="element-373">
                          {overdue ? 'Return Overdue' : 'Return Deadline'}
                        </h3>
                        <p className={`text-sm mt-1 ${overdue ? 'text-red-700' : 'text-orange-700'}`} data-id="element-374">
                          {overdue ? <>
                              Deadline was {booking.returnDeadline}. Please
                              return as soon as possible.
                            </> : daysUntil !== null && daysUntil <= 2 ? <>
                              Return by {booking.returnDeadline} ({daysUntil}{' '}
                              {daysUntil === 1 ? 'day' : 'days'} remaining)
                            </> : <>Return by {booking.returnDeadline}</>}
                        </p>
                      </div>
                    </div>
                  </div>}

                {/* Return Location */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-id="element-375">
                  <h3 className="font-semibold text-slate-900 mb-3" data-id="element-376">
                    Return Location
                  </h3>
                  <div className="space-y-2" data-id="element-377">
                    <div className="flex items-start gap-2" data-id="element-378">
                      <Home size={16} className="text-slate-600 mt-0.5" data-id="element-379" />
                      <div data-id="element-380">
                        <div className="font-medium text-slate-900" data-id="element-381">
                          Stanthorpe Waste Management Facility
                        </div>
                        <div className="text-sm text-slate-600" data-id="element-382">
                          Stanthorpe, Queensland
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600" data-id="element-383">
                      <Clock size={16} className="text-slate-600" data-id="element-384" />
                      <span data-id="element-385">Hours: Monday - Friday, 8:00 AM - 4:00 PM</span>
                    </div>
                  </div>
                </div>

                {/* Instructions */}
                <div data-id="element-386">
                  <h3 className="font-semibold text-slate-900 mb-2" data-id="element-387">
                    Return Checklist
                  </h3>
                  <ul className="space-y-2 text-sm text-slate-600" data-id="element-388">
                    <li className="flex items-start gap-2" data-id="element-389">
                      <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-390" />
                      <span data-id="element-391">
                        Remove all plastic, plant material, soil and debris
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-392">
                      <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-393" />
                      <span data-id="element-394">Brush rollers and moving parts</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-395">
                      <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-396" />
                      <span data-id="element-397">Check undercarriage and tow hitch</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-398">
                      <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-399" />
                      <span data-id="element-400">
                        Ensure no loose material remains that could fall off
                        during transport
                      </span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-401">
                      <CheckCircle size={16} className="text-emerald-500 mt-0.5 shrink-0" data-id="element-402" />
                      <span data-id="element-403">
                        Return equipment to the agreed location in clean
                        condition
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Why Return? */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-404">
                  <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2" data-id="element-405">
                    <AlertCircle size={16} data-id="element-406" />
                    Why return now?
                  </h3>
                  <p className="text-sm text-blue-800" data-id="element-407">
                    There are no bookings scheduled within the next 2 weeks.
                    Returning the equipment to WMF ensures it's secure and
                    available for future bookings.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3 pt-4 border-t border-slate-200" data-id="element-408">
                  <button onClick={handleGetDirections} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors" data-id="element-409">
                    <MapPin size={18} data-id="element-410" />
                    <span data-id="element-411">Get Directions to WMF</span>
                  </button>

                  <button onClick={handleNotifyReturn} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-slate-700 border border-slate-300 rounded-lg font-medium hover:bg-slate-50 transition-colors" data-id="element-412">
                    <CheckCircle size={18} data-id="element-413" />
                    <span data-id="element-414">Notify of Return</span>
                  </button>
                </div>

                {/* Note */}
                <p className="text-xs text-slate-500 text-center" data-id="element-415">
                  Notification is optional but helps track equipment location
                </p>
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}